import { EMetricTypes } from '@src/emp-3.0/types';
import { get } from 'lodash';

export const timeoutUnitOptions = [
  {
    label: 'Minutes',
    value: 'minutes',
  },
  {
    label: 'Seconds',
    value: 'seconds',
  },
];

export const FLOW_FORM_CONFIG = [
  {
    key: ['name'],
    required: true,
  },
  {
    key: ['definition', 'settings', 'timeout'],
    required: true,
  },
  {
    key: ['definition', 'settings', 'timeout_unit'],
    required: true,
  },
  {
    key: ['definition', 'settings', 'complete_timeout'],
    required: true,
  },
  {
    key: ['definition', 'settings', 'complete_timeout_unit'],
    required: true,
  },
  {
    key: ['definition', 'settings', 'threshold', 't1'],
    required: true,
  },
  {
    key: ['definition', 'settings', 'threshold', 't2'],
    required: true,
  },
  {
    key: ['definition', 'settings', 'distribution', 'min'],
    required: true,
  },
  {
    key: ['definition', 'settings', 'distribution', 'max'],
    required: true,
  },
  {
    key: ['definition', 'events', 'start_event'],
    required: true,
  },
  {
    key: ['definition', 'events', 'success_event'],
    required: true,
  },
];

export const PATTERN_FORM_CONFIG = [
  {
    key: ['name'],
    required: true,
  },
  {
    key: ['definition', 'flow_sql_definition'],
    required: true,
  },
  {
    key: ['definition', 'settings', 'timeout'],
    required: true,
  },
  {
    key: ['definition', 'settings', 'timeout_unit'],
    required: true,
  },
  {
    key: ['definition', 'settings', 'complete_timeout'],
    required: true,
  },
  {
    key: ['definition', 'settings', 'complete_timeout_unit'],
    required: true,
  },
  {
    key: ['definition', 'settings', 'threshold', 't1'],
    required: true,
  },
  {
    key: ['definition', 'settings', 'threshold', 't2'],
    required: true,
  },
  {
    key: ['definition', 'settings', 'distribution', 'min'],
    required: true,
  },
  {
    key: ['definition', 'settings', 'distribution', 'max'],
    required: true,
  },
];

export const INDIVIDUAL_METRIC_CONFIG = [
  {
    key: ['name'],
    required: true,
  },
  {
    key: ['definition', 'event'],
    required: true,
    dependencies: (v) => v.metrics_type === EMetricTypes.SINGLE_EVENT,
  },
  {
    key: ['definition', 'first_event'],
    required: true,
    dependencies: (v) => v.metrics_type !== EMetricTypes.SINGLE_EVENT,
  },
  {
    key: ['definition', 'second_event'],
    required: true,
    dependencies: (v) => v.metrics_type !== EMetricTypes.SINGLE_EVENT,
  },
  {
    key: ['definition', 'distribution', 'min_value'],
    required: true,
    dependencies: (v) => {
      const distribution = get(v, ['definition', 'need_distribution']);
      return distribution && v.metrics_type === EMetricTypes.DURATION;
    },
  },
  {
    key: ['definition', 'distribution', 'max_value'],
    required: true,
    dependencies: (v) => {
      const distribution = get(v, ['definition', 'need_distribution']);
      return distribution && v.metrics_type === EMetricTypes.DURATION;
    },
  },
  {
    key: ['definition', 'within_range'],
    required: true,
    dependencies: (v) => v.metrics_type === EMetricTypes.CONVERSION,
  },
  {
    key: ['definition', 'is_higher_better'],
    required: true,
    dependencies: (v) =>
      v.metrics_type === EMetricTypes.CONVERSION || v.metrics_type === EMetricTypes.SINGLE_EVENT,
  },
  {
    key: ['definition', 'attribute'],
    required: true,
    dependencies: (v) => {
      const calculation = get(v, ['definition', 'calculation']);
      return calculation !== 'count' && v.metrics_type === EMetricTypes.SINGLE_EVENT;
    },
  },
  {
    key: ['definition', 'unit'],
    required: true,
    dependencies: (v) => {
      const calculation = get(v, ['definition', 'calculation']);
      const _unit_ = get(v, ['_unit_']);
      return (
        (calculation === 'sum' || calculation === 'average') &&
        _unit_ &&
        v.metrics_type === EMetricTypes.SINGLE_EVENT
      );
    },
  },
];

export const RATIO_METRIC_CONFIG = [
  {
    key: ['name'],
    required: true,
  },
  {
    key: ['definition', 'numerator', 'used_metric_id'],
    required: true,
    dependencies: (v) => {
      const numerator = get(v, ['definition', 'numerator']);
      return (
        numerator !== undefined && Object.prototype.hasOwnProperty.call(numerator, 'used_metric_id')
      );
    },
  },
  {
    key: ['definition', 'numerator', 'event'],
    required: true,
    dependencies: (v) => {
      const numerator = get(v, ['definition', 'numerator']);
      return numerator !== undefined && Object.prototype.hasOwnProperty.call(numerator, 'event');
    },
  },
  {
    key: ['definition', 'numerator', 'attribute'],
    required: true,
    dependencies: (v) => {
      const numerator = get(v, ['definition', 'numerator']);
      const isBuildFromEvent =
        numerator !== undefined && Object.prototype.hasOwnProperty.call(numerator, 'event');
      if (!isBuildFromEvent) {
        return false;
      }
      const { calculation } = numerator;
      return calculation !== 'count';
    },
  },
  {
    key: ['definition', 'denominator', 'used_metric_id'],
    required: true,
    dependencies: (v) => {
      const denominator = get(v, ['definition', 'denominator']);
      return (
        denominator !== undefined &&
        Object.prototype.hasOwnProperty.call(denominator, 'used_metric_id')
      );
    },
  },
  {
    key: ['definition', 'denominator', 'event'],
    required: true,
    dependencies: (v) => {
      const denominator = get(v, ['definition', 'denominator']);
      return (
        denominator !== undefined && Object.prototype.hasOwnProperty.call(denominator, 'event')
      );
    },
  },
  {
    key: ['definition', 'denominator', 'attribute'],
    required: true,
    dependencies: (v) => {
      const denominator = get(v, ['definition', 'denominator']);
      const isBuildFromEvent =
        denominator !== undefined && Object.prototype.hasOwnProperty.call(denominator, 'event');
      if (!isBuildFromEvent) {
        return false;
      }
      const { calculation } = denominator;
      return calculation !== 'count';
    },
  },
  {
    key: ['definition', 'denominator', 'fixed_number'],
    required: true,
    dependencies: (v) => {
      const denominator = get(v, ['definition', 'denominator']);
      return (
        denominator !== undefined &&
        Object.prototype.hasOwnProperty.call(denominator, 'fixed_number')
      );
    },
  },
  // {
  //   key: ['definition', 'unit'],
  //   required: true,
  // },
  {
    key: ['definition', 'is_higher_better'],
    required: true,
  },
];

export const DIMENSION_CONFIG = [
  {
    key: ['display_name'],
    required: true,
  },
];

export const RENAME_CONFIG = [
  {
    key: ['transform_name'],
    required: true,
  },
];

export const DEFAULT_ERROR_EVENTS_NAME = ['5xx server error', 'app crash', 'conviva_anr'];
